import * as ort from 'onnxruntime-web/wasm';
import ortWasmUrl from '../../node_modules/onnxruntime-web/dist/ort-wasm-simd-threaded.wasm?url';

// Keep the browser runtime on the most compatible path. The Live view does not
// need a threaded WASM pool and local/dev servers are not necessarily
// cross-origin isolated.
ort.env.wasm.numThreads = 1;
ort.env.wasm.proxy = false;
ort.env.wasm.wasmPaths = { wasm: ortWasmUrl };

export interface BrowserDetection {
  label: string;
  confidence: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface BrowserInferenceResult {
  detections: BrowserDetection[];
  latencyMs: number;
  provider: 'wasm';
}

const sessions = new Map<string, Promise<ort.InferenceSession>>();

const getSession = (modelUrl: string) => {
  let session = sessions.get(modelUrl);
  if (!session) {
    session = ort.InferenceSession.create(modelUrl, {
      executionProviders: ['wasm'],
      graphOptimizationLevel: 'all'
    }).catch((error) => {
      sessions.delete(modelUrl);
      console.error('[AQL Vision] Não foi possível iniciar o modelo ONNX no browser.', error);
      throw new Error('O motor ONNX não conseguiu iniciar neste browser. Atualiza a página e tenta novamente.');
    });
    sessions.set(modelUrl, session);
  }
  return session;
};

const prepareImage = (image: CanvasImageSource, size: number) => {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('Não foi possível preparar o frame para inferência.');
  context.drawImage(image, 0, 0, size, size);
  const pixels = context.getImageData(0, 0, size, size).data;
  const data = new Float32Array(3 * size * size);
  const plane = size * size;
  for (let index = 0; index < plane; index += 1) {
    data[index] = pixels[index * 4] / 255;
    data[plane + index] = pixels[index * 4 + 1] / 255;
    data[plane * 2 + index] = pixels[index * 4 + 2] / 255;
  }
  return new ort.Tensor('float32', data, [1, 3, size, size]);
};

const parseYoloOutput = (
  tensor: ort.Tensor,
  labels: string[],
  threshold: number,
  inputSize: number
): BrowserDetection[] => {
  if (!(tensor.data instanceof Float32Array) || tensor.dims.length !== 3) return [];
  const [, first, second] = tensor.dims.map(Number);
  const channelsFirst = first < second;
  const channels = channelsFirst ? first : second;
  const candidates = channelsFirst ? second : first;
  if (channels < 5) return [];
  const at = (candidate: number, channel: number) => channelsFirst
    ? tensor.data[channel * candidates + candidate] as number
    : tensor.data[candidate * channels + channel] as number;
  const detections: BrowserDetection[] = [];

  for (let candidate = 0; candidate < candidates; candidate += 1) {
    let bestClass = 0;
    let bestScore = -Infinity;
    for (let channel = 4; channel < channels; channel += 1) {
      const score = at(candidate, channel);
      if (score > bestScore) {
        bestScore = score;
        bestClass = channel - 4;
      }
    }
    if (bestScore < threshold) continue;
    const cx = at(candidate, 0) / inputSize;
    const cy = at(candidate, 1) / inputSize;
    const width = at(candidate, 2) / inputSize;
    const height = at(candidate, 3) / inputSize;
    detections.push({
      label: labels[bestClass] ?? `Classe ${bestClass + 1}`,
      confidence: bestScore,
      x: Math.max(0, cx - width / 2),
      y: Math.max(0, cy - height / 2),
      width: Math.min(1, width),
      height: Math.min(1, height)
    });
  }

  return detections.sort((a, b) => b.confidence - a.confidence).slice(0, 50);
};

export async function runBrowserOnnxInference(params: {
  modelUrl: string;
  image: CanvasImageSource;
  labels: string[];
  inputSize: number;
  confidenceThreshold: number;
}): Promise<BrowserInferenceResult> {
  const startedAt = performance.now();
  const session = await getSession(params.modelUrl);
  const input = prepareImage(params.image, params.inputSize);
  const inputName = session.inputNames[0];
  if (!inputName) throw new Error('O modelo ONNX não declara uma entrada compatível.');
  const outputs = await session.run({ [inputName]: input });
  const output = outputs[session.outputNames[0]];
  if (!output) throw new Error('O modelo ONNX não devolveu uma saída.');
  return {
    detections: parseYoloOutput(output, params.labels, params.confidenceThreshold, params.inputSize),
    latencyMs: performance.now() - startedAt,
    provider: 'wasm'
  };
}
